import { useState } from "react";
import { useForm } from "react-hook-form";
import DataTypes from "~data";

import Form from "../../components/forms/Form";
import FormInput from "../../components/forms/FormInput";
import Input from "../../components/forms/Input";
import Button from "../../components/ui/Button";
import Panel from "../../components/ui/Panel";
import { ApiRequests } from "../../data/api-endpoints";
import { PRODUCT_NAME } from "../../formatters/billing";
import t from "../../i18n";
import { useAuthentication } from "../../providers/AuthenticationProvider";
import { useModal } from "../../providers/ModalProvider";
import { useTrackUnsavedChanges } from "../../providers/UnsavedChangesProvider";
import { useApi } from "../../use/api";
import useRedirect from "../../use/redirect";

type Props = {
  billingOwnerId?: DataTypes["Teams"]["id"];
  onDone: () => Promise<void> | void;
};

export default function NewTeam({ billingOwnerId, onDone }: Props) {
  const { teams, user } = useAuthentication();
  const { confirm } = useModal();
  const redirect = useRedirect();

  const { call: createTeam, isLoading } = useApi("up.teams.create");
  const [error, setError] = useState<string>();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    reset,
    setValue,
  } = useForm<Omit<ApiRequests["up.teams.create"]["team"], "billingOwnerId">>({
    defaultValues: { subIdentifier: "" },
  });
  const { markSaved } = useTrackUnsavedChanges(isDirty);

  async function onSubmit(teamData: Omit<ApiRequests["up.teams.create"]["team"], "billingOwnerId">) {
    const response = await createTeam({
      team: {
        billingOwnerId,
        subIdentifier: teamData.subIdentifier,
      },
    });
    setError(undefined);

    if (response.success) {
      await onDone();
      reset();
      if (!billingOwnerId) {
        markSaved();
        redirect(["hs.documentSharing.present", { teamDomain: response.team.domain }]);
      }
      return;
    }

    switch (response.error) {
      case "domain_already_in_use":
        setError(t("This domain is already being used"));
        return;
      case "invalid_billing_owner":
        void confirm({
          content: t("This parent team cannot have subaccounts."),
          title: t("Invalid parent team"),
          type: "warning",
        });
        return;
      case "saml_user":
        void confirm({
          content: t("Please ask your team administrator to create the new team and invite you to it."),
          title: t("You cannot create new teams"),
          type: "warning",
        });
        return;
      default:
        response.error satisfies never;
    }
  }

  return (
    <Panel>
      <Panel.Header>
        <Panel.Left>
          {billingOwnerId ? (
            <Panel.Title>Create new {teams!.find((t) => t.id === billingOwnerId)!.domain} subaccount</Panel.Title>
          ) : (
            <Panel.Title>Create new {PRODUCT_NAME} team</Panel.Title>
          )}
        </Panel.Left>
        <Panel.Right>
          <Button onClick={onDone} variant="action">
            Cancel
          </Button>
        </Panel.Right>
      </Panel.Header>
      <Panel.Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormInput error={error} label="New team name">
            <Input
              {...register("subIdentifier", {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setError(undefined);
                  const sanitized = e.target.value.replace(/[^a-z0-9-]/g, "");
                  if (e.target.value !== sanitized) setValue("subIdentifier", sanitized);
                },
              })}
              autoFocus
              prefix={user!.freeEmailDomain ? `${user!.username}-` : undefined}
              required
              suffix={user!.freeEmailDomain ? undefined : `.${user!.emailDomain}`}
              type="text"
            />
          </FormInput>
          <Button disabled={!isDirty} loading={isLoading} type="submit">
            Create team
          </Button>
        </Form>
      </Panel.Content>
    </Panel>
  );
}
